import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '@/components/admin/shared/EmptyState';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { PortalPageHeader } from '@/components/client/ClientPortal/PortalPageHeader';
import { cn } from '@/lib/utils';
import {
  OWN_ANNOUNCEMENTS_PAGE_SIZE,
  useOwnAnnouncementByIdQuery,
  useOwnAnnouncementsQuery,
} from '@/hooks/useOwnAnnouncements';
import { ANNOUNCEMENT_QUERY_PARAMETER, buildAnnouncementElementId } from './announcementDeepLink';
import { AnnouncementItem } from './AnnouncementItem';
import { useAnnouncementSenderName } from '@/hooks/useAnnouncementSenderName';

export function AnnouncementsPage() {
  const { t } = useTranslation('clients');
  const senderName = useAnnouncementSenderName();
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();
  const highlightedAnnouncementId = searchParams.get(ANNOUNCEMENT_QUERY_PARAMETER);
  const announcementsQuery = useOwnAnnouncementsQuery(page, OWN_ANNOUNCEMENTS_PAGE_SIZE);
  const announcementsPage = announcementsQuery.data;
  const isHighlightedAnnouncementOnPage =
    announcementsPage !== undefined &&
    announcementsPage.items.some(function matchesHighlightedId(announcement) {
      return announcement.id === highlightedAnnouncementId;
    });
  const pinnedAnnouncementId =
    highlightedAnnouncementId !== null &&
    announcementsPage !== undefined &&
    isHighlightedAnnouncementOnPage === false
      ? highlightedAnnouncementId
      : null;
  const pinnedAnnouncementQuery = useOwnAnnouncementByIdQuery(pinnedAnnouncementId);
  const pinnedAnnouncement = pinnedAnnouncementQuery.data;

  useEffect(
    function scrollToHighlightedAnnouncement() {
      if (
        highlightedAnnouncementId === null ||
        announcementsPage === undefined ||
        isHighlightedAnnouncementOnPage === false
      ) {
        return;
      }
      const targetElement = document.getElementById(
        buildAnnouncementElementId(highlightedAnnouncementId)
      );
      if (targetElement === null) {
        return;
      }
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    [highlightedAnnouncementId, announcementsPage, isHighlightedAnnouncementOnPage]
  );

  return (
    <div className="p-4 sm:p-6 md:p-8">
      <PortalPageHeader title={t('announcements.title')} helperText={t('announcements.helper')} />
      <div className="max-w-4xl">
        {announcementsQuery.isLoading === true ? (
          <div className="py-16 flex justify-center">
            <LoadingSpinner className="w-7 h-7" />
          </div>
        ) : announcementsQuery.isError === true || announcementsPage === undefined ? (
          <QueryErrorState
            onRetry={function retry() {
              announcementsQuery.refetch();
            }}
          />
        ) : (
          <>
            {pinnedAnnouncement !== undefined && pinnedAnnouncement !== null ? (
              <div
                id={buildAnnouncementElementId(pinnedAnnouncement.id)}
                className="scroll-mt-24 mb-3"
              >
                <Card className="ring-2 ring-zooprimary bg-zoovpalelavender">
                  <AnnouncementItem announcement={pinnedAnnouncement} senderName={senderName} />
                </Card>
              </div>
            ) : null}
            {announcementsPage.items.length === 0 ? (
              <EmptyState
                iconName="Megaphone"
                title={t('announcements.emptyTitle')}
                description={t('announcements.empty')}
              />
            ) : (
              <div className="space-y-3">
                {announcementsPage.items.map(function renderAnnouncement(announcement) {
                  const isHighlighted = announcement.id === highlightedAnnouncementId;
                  return (
                    <div
                      key={announcement.id}
                      id={buildAnnouncementElementId(announcement.id)}
                      className="scroll-mt-24"
                    >
                      <Card
                        className={cn(
                          isHighlighted === true ? 'ring-2 ring-zooprimary bg-zoovpalelavender' : ''
                        )}
                      >
                        <AnnouncementItem announcement={announcement} senderName={senderName} />
                      </Card>
                    </div>
                  );
                })}
              </div>
            )}
            <ListPagination
              page={page}
              pageSize={OWN_ANNOUNCEMENTS_PAGE_SIZE}
              totalItems={announcementsPage.totalItems}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}
